import { compact, includes, values } from 'lodash';
import { observer } from 'mobx-react';
import { PAYMENT_TYPES } from '@nandosaus/constants';
import { useRootStore } from '@nandosaus/state-management';
import PropTypes from 'prop-types';

import { Box, Flex } from '../grid';
import { CardComponent } from '../card-component';
import { Divider } from '../divider';
import { GiftCardEntry } from './gift-card-entry';
import { InputList } from '../input-list';
import { P } from '../typography';
import { PeriPerks } from './peri-perks';
import { SelectableButton } from '../selectable-button';
import { useFeature } from '../../hooks/use-feature';

const Payment = ({ hiddenPaymentTypes, hideTitle }) => {
  const { CartStore, PaymentStore, MemberStore } = useRootStore();

  const { isGooglePaySupported, isApplePaySupported, isPaypalSupported } = PaymentStore.alternativePaymentMethods;
  const enableGooglePay = useFeature('pay-with-google-pay');
  const enableApplePay = useFeature('pay-with-apple-pay');
  const enablePaypal = useFeature('pay-with-paypal');

  const hasNonZeroTotalPrice = CartStore.totalPrices.cents > 0;
  const canPayWithPayPal = enablePaypal && isPaypalSupported && hasNonZeroTotalPrice;
  const canPayWithGooglePay = enableGooglePay && isGooglePaySupported && hasNonZeroTotalPrice;
  const canPayWithApplePay = enableApplePay && isApplePaySupported && hasNonZeroTotalPrice;

  const PAYMENT_ITEMS = compact([
    canPayWithPayPal && {
      label: 'PayPal',
      value: PAYMENT_TYPES.PAYPAL,
      iconName: 'paypal',
    },
    {
      label: 'Credit/Debit card',
      value: PAYMENT_TYPES.CREDIT_CARD,
      iconName: 'card',
      children: <CardComponent />,
    },
    canPayWithGooglePay && {
      label: 'Google Pay',
      value: PAYMENT_TYPES.GOOGLE_PAY,
      iconName: 'googleLogo',
      iconWidth: '32px',
    },
    canPayWithApplePay && {
      label: 'Apple Pay',
      value: PAYMENT_TYPES.APPLE_PAY,
      iconName: 'applePay',
      iconWidth: '32px',
    },
  ]).filter(paymentItem => !includes(hiddenPaymentTypes, paymentItem.value));

  const showPeriPoints = MemberStore.isSignedIn && !includes(hiddenPaymentTypes, PAYMENT_TYPES.LOYALTY);
  const showGiftCard = !includes(hiddenPaymentTypes, PAYMENT_TYPES.GIFT_CARD);

  const showRedeemableButtonSection = showPeriPoints && showGiftCard;

  const showGiftCardSection =
    CartStore.hasPaymentType(PAYMENT_TYPES.GIFT_CARD) || (!showRedeemableButtonSection && showGiftCard);
  const showPeriPointsSection =
    CartStore.hasPaymentType(PAYMENT_TYPES.LOYALTY) || (!showRedeemableButtonSection && showPeriPoints);

  const redeemableCoversEntireAmount =
    (CartStore.hasPaymentType(PAYMENT_TYPES.GIFT_CARD) && CartStore.splitAmountNonGiftCard === 0) ||
    CartStore.hasPaymentType(PAYMENT_TYPES.LOYALTY);

  const giftCardSelected = CartStore.hasPaymentType(PAYMENT_TYPES.GIFT_CARD) && CartStore.payment.giftCard;

  return (
    <>
      {(showRedeemableButtonSection || showGiftCardSection || showPeriPointsSection) && (
        <P variant={2} fontWeight="bold" mb={1} mt={2}>
          Redeem
        </P>
      )}

      {showRedeemableButtonSection && (
        <>
          <Flex width="100%" justifyContent="space-between" mb={1}>
            <Box mr="0.5rem" width="100%">
              <SelectableButton
                isSelected={CartStore.hasPaymentType(PAYMENT_TYPES.GIFT_CARD)}
                iconName="giftCard"
                onClick={() => {
                  if (CartStore.hasPaymentType(PAYMENT_TYPES.GIFT_CARD)) {
                    CartStore.removeSplitPaymentType(PAYMENT_TYPES.GIFT_CARD);
                  } else {
                    CartStore.setSplitPaymentType(PAYMENT_TYPES.GIFT_CARD);
                  }
                }}
              >
                Gift Card
              </SelectableButton>
            </Box>

            <Box ml="0.5rem" width="100%">
              <SelectableButton
                isSelected={CartStore.hasPaymentType(PAYMENT_TYPES.LOYALTY)}
                iconName="flame"
                onClick={() => {
                  if (CartStore.hasPaymentType(PAYMENT_TYPES.LOYALTY)) {
                    CartStore.removeSplitPaymentType(PAYMENT_TYPES.LOYALTY);
                  } else {
                    CartStore.setSplitPaymentType(PAYMENT_TYPES.LOYALTY);
                  }
                }}
              >
                PERi-Perks Points
              </SelectableButton>
            </Box>
          </Flex>
        </>
      )}

      {showGiftCardSection && <GiftCardEntry />}
      {showPeriPointsSection && <PeriPerks />}

      {!redeemableCoversEntireAmount && (
        <>
          {!hideTitle && (
            <>
              <Divider my={2} variant="lightGrey" />
              <P variant={2} fontWeight="bold" mb={1} mt={1}>
                {`Select a payment method for ${giftCardSelected ? 'remaining ' : ''} ${
                  CartStore.splitAmountNonGiftCardFormatted
                }`}
              </P>
            </>
          )}
          <InputList
            type="payment"
            items={PAYMENT_ITEMS}
            selectedItems={CartStore.paymentTypes}
            onSelect={CartStore.setSplitPaymentType}
          />
        </>
      )}
    </>
  );
};

Payment.propTypes = {
  hiddenPaymentTypes: PropTypes.arrayOf(PropTypes.oneOf(values(PAYMENT_TYPES))),
  hideTitle: PropTypes.bool,
};
Payment.defaultProps = { hiddenPaymentTypes: [], hideTitle: false };

const PaymentsObserved = observer(Payment);
export { PaymentsObserved as Payment };
